import {
  Component,
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react'
import { Experience } from './components/Experience'
import { useCinematicDirector } from './hooks/useCinematicDirector'
import type { Locale } from './lib/sceneStore'

const CinematicStage = lazy(() =>
  import('./three/CinematicStage').then((module) => ({
    default: module.CinematicStage,
  })),
)

interface SceneBoundaryProps {
  children: ReactNode
}

interface SceneBoundaryState {
  failed: boolean
}

class SceneBoundary extends Component<SceneBoundaryProps, SceneBoundaryState> {
  state: SceneBoundaryState = { failed: false }

  static getDerivedStateFromError(): SceneBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('Cinematic stage failed; poster fallback selected.', error, info)
    }
  }

  render() {
    if (this.state.failed) {
      return <SceneFallback />
    }

    return this.props.children
  }
}

function SceneFallback() {
  return (
    <div className="webgl-fallback" aria-hidden="true">
      <div className="webgl-fallback__halo" />
      <div className="webgl-fallback__core">
        <span />
        <span />
        <span />
      </div>
    </div>
  )
}

function getInitialLocale(): Locale {
  const params = new URLSearchParams(window.location.search)
  const queryLocale = params.get('lang')
  if (queryLocale === 'ar' || queryLocale === 'en') return queryLocale

  const savedLocale = window.localStorage.getItem('avatar-locale')
  if (savedLocale === 'ar' || savedLocale === 'en') return savedLocale

  return navigator.language.toLowerCase().startsWith('ar') ? 'ar' : 'en'
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(getInitialLocale)
  const director = useCinematicDirector(locale)

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
    window.localStorage.setItem('avatar-locale', locale)
  }, [locale])

  const appClassName = useMemo(
    () =>
      [
        'app-shell',
        `app-shell--${locale}`,
        director.verticalCapture ? 'is-vertical-capture' : '',
        director.reducedMotion ? 'is-reduced-motion' : '',
        `quality-${director.quality}`,
      ]
        .filter(Boolean)
        .join(' '),
    [director.quality, director.reducedMotion, director.verticalCapture, locale],
  )

  return (
    <div className={appClassName} data-active-act={director.activeAct}>
      {director.quality === 'poster' ? (
        <SceneFallback />
      ) : (
        <SceneBoundary>
          <Suspense fallback={<SceneFallback />}>
            <CinematicStage quality={director.quality} />
          </Suspense>
        </SceneBoundary>
      )}

      <Experience
        locale={locale}
        activeAct={director.activeAct}
        progress={director.progress}
        demoRequested={director.demoRequested && !director.reducedMotion}
        demoActive={director.demoActive}
        demoPaused={director.demoPaused}
        verticalCapture={director.verticalCapture}
        onLocaleChange={setLocale}
        onDemoToggle={director.toggleDemoPause}
        onDemoRestart={director.restartDemo}
      />
    </div>
  )
}
